import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
    Autocomplete,
    TextField,
    CircularProgress,
    Grid,
} from "@mui/material";
import { Close, CloudUpload } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import userService from "../../services/userService";
import unclaimedPackageService from "../../services/unclaimedPackageService";

const emptyForm = {
    trackingNumber: "",
    dockReceiptNumber: "",
    carrier: "",
    description: "",
    weightLb: "",
    cbf: "",
    notes: "",
};

const UnclaimedPackageDialog = ({ open, onClose, onSaved, editingPackage }) => {
    const { enqueueSnackbar } = useSnackbar();
    const isEdit = Boolean(editingPackage);

    const [form, setForm] = useState(emptyForm);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerInput, setCustomerInput] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editingPackage) {
            setForm({
                trackingNumber: editingPackage.trackingNumber || "",
                dockReceiptNumber: editingPackage.dockReceiptNumber || "",
                carrier: editingPackage.carrier || "",
                description: editingPackage.description || "",
                weightLb: editingPackage.weightLb ?? "",
                cbf: editingPackage.cbf ?? "",
                notes: editingPackage.notes || "",
            });
            setSelectedCustomer(editingPackage.possibleCustomer || null);
        } else {
            setForm(emptyForm);
            setSelectedCustomer(null);
        }
        setCustomerInput("");
        setCustomerOptions([]);
        setPhotoFile(null);
    }, [open, editingPackage]);

    useEffect(() => {
        if (!open) return;
        const query = customerInput.trim();
        if (query.length < 2) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        const timer = setTimeout(() => {
            userService
                .getAll({ role: "customer", search: query, limit: 20 })
                .then(({ data }) => setCustomerOptions(data.data || []))
                .catch(() => setCustomerOptions([]))
                .finally(() => setCustomerLoading(false));
        }, 1000);
        return () => clearTimeout(timer);
    }, [customerInput, open]);

    const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const uploadPhoto = async (packageId) => {
        if (!photoFile) return;
        const { data } = await unclaimedPackageService.presignPhoto(packageId, photoFile.name, photoFile.type);
        const { key, url } = data.data;
        await fetch(url, { method: "PUT", headers: { "Content-Type": photoFile.type }, body: photoFile });
        await unclaimedPackageService.finalizePhoto(packageId, key);
    };

    const handleSave = async () => {
        if (!form.trackingNumber.trim()) {
            enqueueSnackbar("Tracking number is required", { variant: "error" });
            return;
        }
        setSaving(true);
        try {
            const payload = {
                dockReceiptNumber: form.dockReceiptNumber || undefined,
                carrier: form.carrier || undefined,
                description: form.description || undefined,
                weightLb: form.weightLb === "" ? undefined : Number(form.weightLb),
                cbf: form.cbf === "" ? undefined : Number(form.cbf),
                notes: form.notes || undefined,
                possibleCustomer: selectedCustomer?._id || null,
            };

            let pkg;
            if (isEdit) {
                const { data } = await unclaimedPackageService.update(editingPackage._id, {
                    ...payload,
                    trackingNumber: form.trackingNumber.trim(),
                });
                pkg = data.data.package;
            } else {
                const { data } = await unclaimedPackageService.create({
                    trackingNumber: form.trackingNumber.trim(),
                    ...payload,
                });
                pkg = data.data.package;
            }

            if (photoFile) await uploadPhoto(pkg._id);

            enqueueSnackbar(isEdit ? "Package updated" : "Unclaimed package logged", { variant: "success" });
            onSaved?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save package", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontWeight={700}>{isEdit ? "Edit Unclaimed Package" : "Log Unclaimed Package"}</Typography>
                <IconButton size="small" onClick={onClose}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tracking Number *"
                            fullWidth
                            value={form.trackingNumber}
                            onChange={setField("trackingNumber")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Dock Receipt # (D/R)"
                            fullWidth
                            value={form.dockReceiptNumber}
                            onChange={setField("dockReceiptNumber")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Carrier" fullWidth value={form.carrier} onChange={setField("carrier")} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            fullWidth
                            value={form.description}
                            onChange={setField("description")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Weight (lb)"
                            type="number"
                            fullWidth
                            value={form.weightLb}
                            onChange={setField("weightLb")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="CBF" type="number" fullWidth value={form.cbf} onChange={setField("cbf")} />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={customerOptions}
                            loading={customerLoading}
                            value={selectedCustomer}
                            onChange={(_, val) => setSelectedCustomer(val)}
                            inputValue={customerInput}
                            onInputChange={(_, val) => setCustomerInput(val)}
                            filterOptions={(x) => x}
                            getOptionLabel={(o) => (o ? `${o.firstName} ${o.lastName} (${o.email})` : "")}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            noOptionsText={
                                customerInput.trim().length < 2 ? "Type at least 2 characters…" : "No customers found"
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Possible Customer" placeholder="Search by name or email…" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={setField("notes")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                            {photoFile ? photoFile.name : "Upload Photo (optional)"}
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                            />
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? "Save Changes" : "Log Package"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UnclaimedPackageDialog;
